import { motion } from "framer-motion";
import { ArrowRight, MessageSquareText } from "lucide-react";
import { EASE, IMAGES, PHONE, PHONE_RAW, nextFridayLabel } from "./shared";
import Footer from "./Footer";

const CHOICES = [
  {
    num: "01",
    href: "/stump-grinding",
    testId: "choose-stumps",
    img: IMAGES.stumpBefore,
    alt: "Backyard tree stump ready for grinding",
    title: "Stump Grinding",
    sub: "One stump or five. Text a photo, get a flat price by the inch, come home Friday to flat ground.",
    tag: "From $125 / stump",
  },
  {
    num: "02",
    href: "/land-clearing",
    testId: "choose-clearing",
    img: IMAGES.forest,
    alt: "Overgrown brush and saplings before forestry mulching",
    title: "Forestry Mulching & Land Clearing",
    sub: "Brush, honeysuckle, fence lines, whole lots — ground into mulch where it stands. No burn pile, no hauling.",
    tag: "Priced by the area",
  },
];

export default function Decision() {
  return (
    <div id="top" data-testid="decision-page" className="flex min-h-screen flex-col">
      <header className="fixed top-0 z-50 w-full border-b border-stone-800/80 bg-[#0c0a09]/85 px-4 py-3 backdrop-blur-xl sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <span className="flex items-center gap-3">
            <span className="font-display text-2xl font-black uppercase leading-none tracking-tight text-bone">
              Friday<span className="text-amber-500">Stumps</span>
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap border border-stone-800 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] text-stone-400">
              <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-amber-500" />
              {nextFridayLabel()}
            </span>
          </span>
          <a
            data-testid="decision-text-link"
            href={`sms:${PHONE_RAW}`}
            className="flex min-h-[44px] items-center gap-2 whitespace-nowrap border border-stone-700 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
          >
            <MessageSquareText className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Text {PHONE}</span>
            <span className="sm:hidden">Text</span>
          </a>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pb-16 pt-28 sm:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500 sm:text-xs"
        >
          Eric Fisher · Columbus, OH · Fridays only · 100% contactless
        </motion.p>

        <h1 data-testid="decision-headline" className="font-display text-3xl font-black uppercase leading-[0.92] tracking-tight min-[400px]:text-4xl sm:text-5xl lg:text-6xl">
          {[
            { text: "WHAT ARE WE", cls: "text-bone" },
            { text: "TURNING TO MULCH?", cls: "italic text-amber-500" },
          ].map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
                className={`block ${line.cls}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
          {CHOICES.map((c, i) => (
            <motion.a
              key={c.href}
              href={c.href}
              data-testid={c.testId}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 + i * 0.15, ease: EASE }}
              className="group relative block min-h-[320px] overflow-hidden border border-stone-800 bg-charcoal transition-colors duration-200 hover:border-amber-500/60 lg:min-h-[420px]"
            >
              <img
                src={c.img}
                alt={c.alt}
                className="absolute inset-0 h-full w-full object-cover opacity-40 contrast-110 saturate-[0.85] transition-opacity duration-300 group-hover:opacity-55"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0a09] via-[#0c0a09]/60 to-[#0c0a09]/20" />
              <div className="relative flex h-full flex-col justify-end p-7 sm:p-9">
                <span className="absolute left-7 top-7 font-mono text-xs tracking-[0.25em] text-amber-500 sm:left-9 sm:top-9">
                  {c.num}
                </span>
                <span className="absolute right-7 top-7 border border-amber-500/50 bg-[#0c0a09]/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 backdrop-blur-sm sm:right-9 sm:top-9">
                  {c.tag}
                </span>
                <h2 className="font-display text-3xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-4xl lg:text-5xl">
                  {c.title}
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-400 sm:text-base">
                  {c.sub}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber-500 transition-transform duration-200 group-hover:translate-x-1">
                  This way <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 font-mono text-[11px] uppercase tracking-[0.18em] text-stone-500"
        >
          Not sure which machine it needs?{" "}
          <a href={`sms:${PHONE_RAW}`} className="text-amber-500 underline-offset-4 hover:underline">
            Text a photo to {PHONE}
          </a>{" "}
          and Eric will tell you.
        </motion.p>
      </main>

      <Footer />
    </div>
  );
}
